import '.a7{position:relative;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.am{height:100%;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.ac{flex-direction:column;}.ssr-plugin'
import '.ah{gap:16px;}.ssr-plugin'
import '.g2{width:160px;}.ssr-plugin'
import '.ba{position:fixed;}.ssr-plugin'
import '.g3{z-index:4;}.ssr-plugin'
import '.au{pointer-events:none;}.ssr-plugin'
import '.g4{padding:6px 10px;}.ssr-plugin'
import '.a1{gap:4px;}.ssr-plugin'
import '.g5{height:12px;}.ssr-plugin'
import '.g6{width:4px;}.ssr-plugin'
import '.ek{flex-shrink:0;}.ssr-plugin'
import '.g7{margin-right:auto;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Color, colors } from '../../tokens/colors.js'
import { measureTextWidth, Text } from '../Text/index.js'
import { getChartColorForNumber } from '../../utils/misc.js'
import { shadows } from '../../utils/shadows.js'
import { radius } from '../../utils/radius.js'
import { generatePath, linearScale } from '../../utils/charts.js'
import { Legend } from '../Legend/index.js'
import React from 'react'
import { TEXT_VARIANT_STYLES } from '../../tokens/typography.js'
import { styled, Style } from 'inlines'

type LineChartProps = {
  data: Record<string, number>[]
  xKey: string
  yKeys: string[]
  xFormat?: (x: number) => string
  yFormat?: (y: number) => string
  hideLegend?: boolean
  hideXLabels?: boolean
  hideYLabels?: boolean
  mono?: boolean
  style?: Style
}

function LineChart({
  data,
  xKey,
  yKeys,
  xFormat,
  yFormat,
  hideLegend = false,
  hideXLabels = false,
  hideYLabels = false,
  mono = false,
  style,
}: LineChartProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const availableDataHeight = useRef<number>(0)
  const [svgRect, setSVGRect] = useState<DOMRect | null>(null)
  const [mouseX, setMouseX] = useState<null | number>(null)
  const [mouseY, setMouseY] = useState<null | number>(null)
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null)

  const _xFormat = useMemo(
    () => xFormat ?? ((x: number) => String(x)),
    [xFormat],
  )
  const _yFormat = useMemo(
    () => yFormat ?? ((y: number) => String(y)),
    [yFormat],
  )

  const [minXRaw, maxXRaw, minYRaw, maxYRaw] = useMemo(() => {
    let minX = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY

    for (const item of data || []) {
      const xv = Number(item?.[xKey])
      if (Number.isFinite(xv)) {
        if (xv < minX) minX = xv
        if (xv > maxX) maxX = xv
      }
      for (const yKey of yKeys) {
        const yv = Number(item?.[yKey])
        if (!Number.isFinite(yv)) continue
        if (yv < minY) minY = yv
        if (yv > maxY) maxY = yv
      }
    }

    return [minX, maxX, minY, maxY]
  }, [xKey, yKeys, data])

  const [minX, maxX, minY, maxY] = useMemo(() => {
    let aMinX = Number.isFinite(minXRaw) ? minXRaw : 0
    let aMaxX = Number.isFinite(maxXRaw) ? maxXRaw : aMinX + 1
    if (aMinX === aMaxX) aMaxX = aMinX + 1

    let aMinY = Number.isFinite(minYRaw) ? minYRaw : 0
    let aMaxY = Number.isFinite(maxYRaw) ? maxYRaw : aMinY + 1
    if (aMinY === aMaxY) aMaxY = aMinY + 1

    return [aMinX, aMaxX, aMinY, aMaxY]
  }, [minXRaw, maxXRaw, minYRaw, maxYRaw])

  const [paddingTop, paddingRight, paddingBottom, paddingLeft] = useMemo(() => {
    const largestLabelCharacterCount = _yFormat(maxY).length
    const pr = hideYLabels
      ? 0
      : largestLabelCharacterCount * measureTextWidth('0', 'subtext-mono') + 8
    return [8, pr, 24, 0]
  }, [_yFormat, maxY, hideYLabels])

  const xScale = useMemo(() => {
    if (!svgRect) return null
    const right = svgRect.width - paddingRight - 4
    const left = paddingLeft
    return linearScale([minX, maxX], [left, right])
  }, [minX, maxX, paddingLeft, svgRect, paddingRight])

  const yScale = useMemo(() => {
    if (!svgRect) return null
    const top = paddingTop
    const bottom = hideXLabels
      ? svgRect.height
      : availableDataHeight.current + 7
    return linearScale([minY, maxY], [bottom, top])
  }, [minY, maxY, paddingTop, svgRect, hideXLabels])

  const points = useMemo(() => {
    if (!xScale || !yScale || !Array.isArray(data) || !data.length) return {}
    const res: Record<string, { x: number; y: number }[]> = {}
    for (const yKey of yKeys) {
      res[yKey] = data.map((item) => {
        const x = Number(item?.[xKey])
        const y = Number(item?.[yKey])
        return { x: xScale(x), y: yScale(y) }
      })
    }
    return res
  }, [data, yKeys, xScale, yScale, xKey])

  const seriesKeys = useMemo(
    () =>
      yKeys.filter((k) => Array.isArray(points[k]) && points[k]!.length >= 1),
    [points, yKeys],
  )

  const hasPoints = seriesKeys.length > 0 && !!svgRect

  const hover = useMemo(() => {
    if (mouseX === null || !xScale || !data?.length) return null
    const xPositions = data
      .map((item) => Number(item?.[xKey]))
      .map((x) => xScale(x))

    if (!xPositions.length) return null

    let idx = 0
    let best = Math.abs(mouseX - xPositions[0]!)
    for (let i = 1; i < xPositions.length; i++) {
      const d = Math.abs(mouseX - xPositions[i]!)
      if (d < best) {
        best = d
        idx = i
      }
    }
    return idx
  }, [mouseX, data, xKey, xScale])

  const hoverMinYKey = useMemo(() => {
    if (hover === null || seriesKeys.length === 0) return ''
    let smallest = Number.POSITIVE_INFINITY
    let key = seriesKeys[0]!
    for (const yKey of seriesKeys) {
      const v = Number(data[hover]?.[yKey])
      if (v < smallest) {
        smallest = v
        key = yKey
      }
    }
    return key
  }, [data, hover, seriesKeys])

  useLayoutEffect(() => {
    const ref = svgRef.current
    if (!ref) return
    const calculate = () => {
      const rect = ref.getBoundingClientRect()
      setSVGRect(rect)
      availableDataHeight.current = rect.height - paddingBottom - 12 - 9
    }
    calculate()
    const resizeObs = new ResizeObserver(calculate)
    resizeObs.observe(ref)
    return () => resizeObs.disconnect()
  }, [paddingBottom])

  const getPoint = (yKey: string, index: number) => {
    const arr = points[yKey]
    if (!arr || !arr.length) return null
    const i = Math.max(0, Math.min(index, arr.length - 1))
    return arr[i]
  }

  const baseKey = seriesKeys[0]!
  const lastPointOf = (yKey: string) => points[yKey]?.[points[yKey].length - 1]

  return (
    <styled.div className="a7 f am a ac ah"
      style={{
        ...style,
      }}
    >
      <svg
        ref={svgRef}
        style={{ width: '100%', height: '100%' }}
        onMouseMove={(e) => {
          if (!svgRect) return
          setMouseX(e.clientX - svgRect.left)
          setMouseY(e.clientY)
        }}
        onMouseLeave={() => setMouseX(null)}
      >
        {svgRect && (
          <>
            {!hideYLabels && (
              <g>
                <line
                  style={{ stroke: colors.neutral10, strokeWidth: 1 }}
                  x1={0}
                  y1={paddingTop}
                  x2={svgRect.width - paddingRight}
                  y2={paddingTop}
                />
                <text
                  x={svgRect.width}
                  y={paddingTop + 4.5}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                    textAnchor: 'end',
                  }}
                >
                  {_yFormat(maxY)}
                </text>

                <line
                  style={{ stroke: colors.neutral10, strokeWidth: 1 }}
                  x1={0}
                  y1={paddingTop + availableDataHeight.current * 0.25}
                  x2={svgRect.width - paddingRight}
                  y2={paddingTop + availableDataHeight.current * 0.25}
                />
                <text
                  x={svgRect.width}
                  y={paddingTop + availableDataHeight.current * 0.25 + 4.5}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                    textAnchor: 'end',
                  }}
                >
                  {_yFormat(minY + (maxY - minY) * 0.75)}
                </text>

                <line
                  style={{ stroke: colors.neutral10, strokeWidth: 1 }}
                  x1={0}
                  y1={paddingTop + availableDataHeight.current * 0.5}
                  x2={svgRect.width - paddingRight}
                  y2={paddingTop + availableDataHeight.current * 0.5}
                />
                <text
                  x={svgRect.width}
                  y={paddingTop + availableDataHeight.current * 0.5 + 4.5}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                    textAnchor: 'end',
                  }}
                >
                  {_yFormat(minY + (maxY - minY) * 0.5)}
                </text>

                <line
                  style={{ stroke: colors.neutral10, strokeWidth: 1 }}
                  x1={0}
                  y1={paddingTop + availableDataHeight.current * 0.75}
                  x2={svgRect.width - paddingRight}
                  y2={paddingTop + availableDataHeight.current * 0.75}
                />
                <text
                  x={svgRect.width}
                  y={paddingTop + availableDataHeight.current * 0.75 + 4.5}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                    textAnchor: 'end',
                  }}
                >
                  {_yFormat(minY + (maxY - minY) * 0.25)}
                </text>

                <line
                  style={{ stroke: colors.neutral10, strokeWidth: 1 }}
                  x1={0}
                  y1={paddingTop + availableDataHeight.current}
                  x2={svgRect.width - paddingRight}
                  y2={paddingTop + availableDataHeight.current}
                />
                <text
                  x={svgRect.width}
                  y={paddingTop + availableDataHeight.current + 4.5}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                    textAnchor: 'end',
                  }}
                >
                  {_yFormat(minY)}
                </text>
              </g>
            )}

            {!hideXLabels && (
              <g>
                <text
                  x={paddingLeft}
                  y={svgRect.height}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                  }}
                >
                  {_xFormat(minX)}
                </text>

                {svgRect.width > 500 && (
                  <>
                    <text
                      x={(svgRect.width - paddingRight) * 0.25}
                      y={svgRect.height}
                      style={{
                        fill: colors.neutral20,
                        ...TEXT_VARIANT_STYLES['subtext-mono'],
                        textAnchor: 'middle',
                      }}
                    >
                      {_xFormat(minX + (maxX - minX) * 0.25)}
                    </text>
                    <text
                      x={(svgRect.width - paddingRight) * 0.5}
                      y={svgRect.height}
                      style={{
                        fill: colors.neutral20,
                        ...TEXT_VARIANT_STYLES['subtext-mono'],
                        textAnchor: 'middle',
                      }}
                    >
                      {_xFormat(minX + (maxX - minX) * 0.5)}
                    </text>
                    <text
                      x={(svgRect.width - paddingRight) * 0.75}
                      y={svgRect.height}
                      style={{
                        fill: colors.neutral20,
                        ...TEXT_VARIANT_STYLES['subtext-mono'],
                        textAnchor: 'middle',
                      }}
                    >
                      {_xFormat(minX + (maxX - minX) * 0.75)}
                    </text>
                  </>
                )}

                <text
                  x={svgRect.width - paddingRight}
                  y={svgRect.height}
                  style={{
                    fill: colors.neutral20,
                    ...TEXT_VARIANT_STYLES['subtext-mono'],
                    textAnchor: 'end',
                  }}
                >
                  {_xFormat(maxX)}
                </text>
              </g>
            )}

            <g>
              {seriesKeys.map((yKey, idx) => {
                const arr = points[yKey]
                const d = arr && arr.length > 1 ? generatePath(arr) : ''
                return (
                  <React.Fragment key={yKey + 'bg'}>
                    <path
                      d={d}
                      fill="none"
                      stroke={colors.neutralInverted100}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d={d}
                      opacity={
                        !highlightedValue
                          ? 1
                          : highlightedValue === yKey
                            ? 1
                            : 0.2
                      }
                      fill="none"
                      stroke={colors[getChartColorForNumber(idx, mono)]}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </React.Fragment>
                )
              })}
            </g>

            {hover !== null &&
              (() => {
                const basePt = getPoint(baseKey, hover)
                if (!basePt) return null
                return (
                  <>
                    <line
                      style={{ stroke: colors.neutral10, strokeWidth: 1 }}
                      x1={basePt.x}
                      y1={5}
                      x2={basePt.x}
                      y2={svgRect.height - 5 - 7.25 - 6}
                    />
                    {seriesKeys.map((yKey, index) => {
                      const p = getPoint(yKey, hover)
                      if (!p) return null
                      return (
                        <g key={yKey + hover}>
                          <circle
                            r="4"
                            style={{
                              fill: colors.neutralInverted100,
                              transformOrigin: '0 0',
                              transform: `translate(${p.x}px, ${p.y}px)`,
                            }}
                          />
                          <circle
                            r="4"
                            style={{
                              fill: colors[getChartColorForNumber(index, mono)],
                              transformOrigin: '0 0',
                              transform: `translate(${p.x}px, ${p.y}px)`,
                            }}
                          />
                          <circle
                            r="2"
                            style={{
                              fill: colors.neutralInverted100,
                              transformOrigin: '0 0',
                              transform: `translate(${p.x}px, ${p.y}px)`,
                            }}
                          />
                        </g>
                      )
                    })}
                  </>
                )
              })()}

            {hover == null &&
              seriesKeys.map((yKey, idx) => {
                const last = lastPointOf(yKey)
                if (!last) return null
                return (
                  <React.Fragment key={yKey + 'jj'}>
                    <circle
                      r="4"
                      style={{
                        fill: colors.neutralInverted100,
                        transformOrigin: '0 0',
                        transform: `translate(${last.x}px, ${last.y}px)`,
                      }}
                    />
                    <circle
                      r="4"
                      style={{
                        opacity: !highlightedValue
                          ? 1
                          : highlightedValue === yKey
                            ? 1
                            : 0.2,
                        fill: colors[getChartColorForNumber(idx, mono)],
                        transformOrigin: '0 0',
                        transform: `translate(${last.x}px, ${last.y}px)`,
                      }}
                    />
                    <circle
                      r="2"
                      style={{
                        fill: colors.neutralInverted100,
                        transformOrigin: '0 0',
                        transform: `translate(${last.x}px, ${last.y}px)`,
                      }}
                    />
                  </React.Fragment>
                )
              })}
          </>
        )}
      </svg>

      {hover !== null && (
        <div className="g2 ba g3 au a ac g4 a1"
          style={{
            top: `${mouseY! + 8}px`,
            left: `${svgRect!.left + mouseX! - 80}px`,
            background: colors.neutralInverted100,
            border: `1px solid  ${colors.neutral20Adjusted}`,
            boxShadow: shadows.popoverSmall,
            borderRadius: radius[8],}}
        >
          <Text singleLine variant="subtext-medium" color="neutral60">
            {_xFormat(
              Number(
                data[Math.max(0, Math.min(hover, data.length - 1))]?.[xKey],
              ),
            )}
          </Text>
          {seriesKeys.map((yKey, idx) => (
            <div className="a a1 f"
              key={yKey}
            >
              <div className="g5 g6"
                style={{
                  background: colors[getChartColorForNumber(idx, mono)],
                  borderRadius: radius[4],
                }}
              />
              <div className="ek g7">
                <Text
                  singleLine
                  variant="subtext-medium"
                  color={getChartColorForNumber(idx, mono)}
                >
                  {yKey}
                </Text>
              </div>
              <Text singleLine variant="subtext-mono" color="neutral100">
                {_yFormat(
                  Number(
                    data[Math.max(0, Math.min(hover, data.length - 1))]?.[yKey],
                  ),
                )}
              </Text>
            </div>
          ))}
        </div>
      )}

      {!hideLegend && (
        <div className="a ah c d"
        >
          {seriesKeys.map((yKey, idx) => (
            <Legend
              key={yKey}
              label={yKey}
              color={getChartColorForNumber(idx, mono)}
              onMouseEnter={() => setHighlightedValue(yKey)}
              onMouseLeave={() => setHighlightedValue(null)}
              style={{
                opacity: !highlightedValue
                  ? 1
                  : highlightedValue === yKey
                    ? 1
                    : 0.2,
              }}
            />
          ))}
        </div>
      )}
    </styled.div>
  )
}

export type { LineChartProps }
export { LineChart }
